//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useEffect, useContext, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import CalendarTodayIcon from '@material-ui/icons/CalendarToday';
import { useLocation } from 'react-router-dom';
import ArrowBackIosIcon from '@material-ui/icons/ArrowBackIos';
import { useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { unsubscribeRealtimeUpdates, logPageView } from '../system/firebase';
import { AppContext, LayoutContext } from '../common/contexts';
import { setClassNameInDefns } from '../common/definitionHelper';
import { DASHBOARD_MY_MESSAGES, PAGE_TITLE_JS_MY_MESSAGES } from '../common/constants';
import { getFormattedDate } from '../common/dateHelper';
import { getUserDateFormat } from '../common/dateHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { loadMyMessagesById, loadThreadSummariesById } from '../common/services/myMessagesServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdPageHead from '../common/elemental/AfitdPageHead';
import AfitdTypography from '../common/elemental/AfitdTypography';
// import SearchThread from '../empDashboardMyMessages/subComponents/SearchThread';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------
import ThreadSummaries from './subComponents/ThreadSummaries';
import ThreadDetail from './subComponents/ThreadDetail';
import Footer from '../dashboard/subComponents/Footer';

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------
// import cx from 'clsx';
import _get from 'lodash/get';
import _reduce from 'lodash/reduce';
import _size from 'lodash/size';

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        root: {
            height: '100%',
            background: '#FAFAFC',
        },
        header: {
            height: '128px',
            [theme.breakpoints.down('md')]: { height: 90 },
        },
        content: {
            flex: 1,
            overflowY: 'hidden',
            marginTop: 0,
            marginBottom: 0,
            borderRadius: 10,
            backgroundColor: '#fff',
        },
        chatRight: {
            overflowY: 'auto',
            height: '100%',
            borderLeft: '1px solid #f1f2f6',
            '&::-webkit-scrollbar-track': {
                borderRadius: '10px',
                background: 'none',
            },
            '&::-webkit-scrollbar': {
                width: '8px',
                backgroundColor: '#F5F5F5',
            },
            '&::-webkit-scrollbar-thumb': {
                borderRadius: '10px',
                backgroundColor: 'rgba(0,0,0,0.2)',
            },
        },

        headerTitle: {
            marginTop: '38px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
            [theme.breakpoints.down('md')]: { marginTop: '16px' },
        },
        headerToday: {
            marginTop: '10px',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-start',
            alignItems: 'center',
        },
        todayIcon: {
            fontSize: '16px',
            color: 'rgba(17, 30, 20, 0.4)',
        },
        todayContainer: {
            marginTop: '1px',
            marginLeft: '9px',
            fontSize: '13px',
            color: 'rgba(17, 30, 20, 0.6)',
        },
        title: {
            fontStyle: 'normal',
            fontWeight: '500',
            fontSize: '26px',
            lineHeight: '30px',
            color: '#111E14',
        },
        confirmedInterviewsContainer: {
            marginTop: '4px',
            marginLeft: '24px',
            display: 'flex',
        },
        unreadMessagesCount: {
            marginRight: '3px',
            color: '#FC821F',
        },
        unreadMessagesCountText: {
            marginRight: '3px',
        },
        chatLeftBlock1: {
            padding: '23px 18px 23px 22px',
        },
        threadSummariesHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: 20,
        },
        navigation: { margin: '10px 0' },
        navigationButtonContainer: { display: 'flex', alignItems: 'center' },
        navigationIconContainer: {
            width: '32px',
            height: '32px',
            background: '#F2F2F4',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            '&:hover': { cursor: 'pointer' },
        },
        navIconLeft: { marginLeft: 11, width: 18 },
        navigationText: { marginLeft: 8, fontSize: 16, fontWeight: 'bold', '&:hover': { cursor: 'pointer' }, marginTop: '-2px' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const {
        classes,
        // props,
        userDateFormat,
        myMessagesData,
        threadSummariesData,
        clickedThreadData,
        setClickedThreadData,
        threadCount,
    } = params;

    const myMessagesId = _get(myMessagesData, 'myMessagesId');
    const currentThreadId = _get(clickedThreadData, 'threadId');
    const totalNewMessagesCount = getTotalNewMessagesCount(threadSummariesData, currentThreadId);

    const clickedThreadId = _get(clickedThreadData, 'threadId');
    const clickedThreadLastReadDt = _get(clickedThreadData, 'lastReadDt');
    const clickedThreadDeleteDt = _get(clickedThreadData, 'deleteDt');

    const defns = {
        root: { item: true, container: true, direction: 'column', lg: 12, md: 12, sm: 12, xs: 12 },
        content: { container: true, item: true, spacing: 0 },
        chatLeft: { item: true, lg: 5, md: 12, sm: 12, xs: 12 },
        chatRight: { item: true, lg: 7, md: 12, sm: 12, xs: 12 },

        navigation: { container: true, direction: 'row' },
        navigationItem: { item: true, xl: 3, lg: 4, md: 11, sm: 11, xs: 11 },

        unreadMessagesCount: {
            variant: 'body2',
            value: `${totalNewMessagesCount}`,
        },
        unreadMessagesCountText: {
            variant: 'body2',
            value: 'Unread Messages',
        },
        todayText: {
            variant: 'caption',
            value: getFormattedDate(new Date(), userDateFormat),
            clsBigCaption: true,
        },
        threadSummaries: {
            data: threadSummariesData,
            threadSummaryOnClick: handleClickThreadSummary.bind(null, setClickedThreadData),
        },
        threadDetail: {
            myMessagesId,
            threadId: clickedThreadId,
            lastReadDt: clickedThreadLastReadDt,
            deleteDt: clickedThreadDeleteDt,
            threadCount,
        },
    };

    setClassNameInDefns(defns, classes);

    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------
const handleClickThreadSummary = (setClickedThreadData, clickedThreadData, currentThreadId) => {
    const threadId = _get(clickedThreadData, 'threadId');
    if (currentThreadId !== threadId) {
        setClickedThreadData(clickedThreadData);
    }
};

const getTotalNewMessagesCount = (data, currentThreadId) => {
    return _reduce(data, (total, thread) => (_get(thread, 'threadId') === currentThreadId ? total : total + _get(thread, 'unreadCount', 0)), 0);
};

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const MyMessages = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();
    const location = useLocation();
    const appContext = useContext(AppContext);
    const context = useContext(LayoutContext);
    const userId = _get(context, 'userId');
    const setCurrentThreadId = _get(context, 'setCurrentThreadId');
    const userDateFormat = getUserDateFormat(appContext);

    const theme = useTheme();
    const mdMatches = useMediaQuery(theme.breakpoints.down('md'));
    const smMatches = useMediaQuery(theme.breakpoints.down('sm'));

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [myMessagesData, setMyMessagesData] = useState();
    const [threadSummariesData, setThreadSummariesData] = useState();
    const [clickedThreadData, setClickedThreadData] = useState();
    const [showMessageDetails, setShowMessageDetails] = useState(false);

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------
    const threadCount = _size(threadSummariesData);

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------
    useEffect(() => {
        logPageView(PAGE_TITLE_JS_MY_MESSAGES);
    }, []);

    useEffect(() => {
        if (userId) {
            // load myMessages for user
            const unsubscribeLoadMyMessagesByIdOnSnapshot = loadMyMessagesById(userId, setMyMessagesData);

            // load all thread summaries for user
            const unsubscribeLoadThreadSummariesByIdOnSnapshot = loadThreadSummariesById(userId, setThreadSummariesData);

            // unsubscribe realtime update on unmount
            return () => {
                unsubscribeRealtimeUpdates([unsubscribeLoadMyMessagesByIdOnSnapshot, unsubscribeLoadThreadSummariesByIdOnSnapshot]);
            };
        }
    }, [userId]);

    // useEffect(() => {
    //     // set initial clickedThreadData in order to load the messages of the first thread
    //     if (threadSummariesData && !clickedThreadData) {
    //         const firstThreadSummaryData = _get(threadSummariesData, '[0]');
    //         setClickedThreadData(firstThreadSummaryData);
    //     }
    // }, [threadSummariesData, clickedThreadData]);

    useEffect(() => {
        const { state } = location;
        if (state?.state?.threadId) {
            const threadFound = threadSummariesData.find(el => el.threadId === state?.state?.threadId);
            setClickedThreadData(threadFound);
        }
    }, [threadSummariesData, location]);

    //---------------------------------------------------------------------
    // useCallback
    //---------------------------------------------------------------------
    const clearCurrentThread = useCallback(() => {
        setClickedThreadData(null);
        setCurrentThreadId(null);
    }, [setCurrentThreadId]);

    //---------------------------------------------------------------------
    // Defns
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, userDateFormat, myMessagesData, threadSummariesData, clickedThreadData, setClickedThreadData, threadCount });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <LayoutContext.Provider value={{ ...context, clearCurrentThread }}>
            <AfitdPageHead title={PAGE_TITLE_JS_MY_MESSAGES} />
            <Grid {...defns.root}>
                {showMessageDetails && mdMatches ? (
                    smMatches ? null : (
                        <Grid {...defns.navigation}>
                            <Grid {...defns.navigationItem}>
                                <div {...defns.navigationButtonContainer} onClick={() => setShowMessageDetails(false)}>
                                    <div {...defns.navigationIconContainer}>
                                        <ArrowBackIosIcon {...defns.navIconLeft} />
                                    </div>
                                    <span {...defns.navigationText}>Back</span>
                                </div>
                            </Grid>
                        </Grid>
                    )
                ) : (
                    <Grid {...defns.header}>
                        <div {...defns.headerTitle}>
                            <div {...defns.title}>{DASHBOARD_MY_MESSAGES}</div>
                            <div {...defns.confirmedInterviewsContainer}>
                                <AfitdTypography {...defns.unreadMessagesCount} />
                                <AfitdTypography {...defns.unreadMessagesCountText} />
                            </div>
                        </div>
                        <div {...defns.headerToday}>
                            <CalendarTodayIcon {...defns.todayIcon} />
                            <div {...defns.todayContainer}>
                                <AfitdTypography {...defns.todayText} />
                            </div>
                        </div>
                    </Grid>
                )}

                <Grid {...defns.content}>
                    {showMessageDetails && mdMatches ? (
                        <Grid {...defns.chatRight}>
                            <ThreadDetail {...defns.threadDetail} setShowMessageDetails={setShowMessageDetails} />
                        </Grid>
                    ) : (
                        <Grid {...defns.chatLeft}>
                            {/* <div {...defns.threadSummariesHeader}>
                                <SearchThread />
                            </div> */}
                            <br />
                            <ThreadSummaries {...defns.threadSummaries} setShowMessageDetails={setShowMessageDetails} />
                        </Grid>
                    )}
                    {/* chat details hide for 0-1023px (mdDown) */}
                    {mdMatches ? null : (
                        <Grid {...defns.chatRight}>
                            <ThreadDetail {...defns.threadDetail} setShowMessageDetails={setShowMessageDetails} />
                        </Grid>
                    )}
                </Grid>
                <Grid {...defns.bottomFooter}>{mdMatches ? <Footer /> : null}</Grid>
            </Grid>
        </LayoutContext.Provider>
    );
};

export default MyMessages;
